import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type RewardsCatalogEntry, bulkUpsertEntries, queryEntries } from '~/services/LuxLoyalty';

import { rewardsQueryKeys } from './rewardsQueryKeys';

/** Query filters for fetching reward entries. */
interface RewardsEntriesFilters {
  catalogId: string;
  itemType: string;
  itemSubType: string;
  itemReferences?: Array<string>;
}

/**
 * Server-side max for `limit` on the queryRewardsEntries endpoint.
 * Matches the Zod schema in svc-lux-loyalty (`admin.schemas.ts`).
 */
const PAGE_SIZE = 500;

/**
 * Hard cap on total entries fetched across pages — prevents runaway pagination
 * if the API ever returns unexpectedly many results. Matches our largest
 * realistic input (200 flights × 4 cabin classes = 800 references).
 */
const MAX_PAGES = 20;

/**
 * Fetches all reward catalog entries matching the filters by paginating until
 * the server returns fewer rows than `PAGE_SIZE`.
 *
 * Pagination is required because a single search can produce more than
 * `PAGE_SIZE` `itemReferences` (e.g. ~200 flights × multiple cabin classes).
 * Without it, configured entries past the first page render as
 * `NOT_CONFIGURED` and a save would overwrite real pricing/status with
 * defaults.
 */
export function useGetRewardsEntries(filters: RewardsEntriesFilters | null) {
  return useQuery({
    queryKey: filters ? rewardsQueryKeys.entriesList(filters) : [...rewardsQueryKeys.entries(), 'disabled'],
    queryFn: async ({ signal }) => {
      if (!filters) {
        return [];
      }

      const all: Array<RewardsCatalogEntry> = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        const batch = await queryEntries(
          {
            ...filters,
            offset: page * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
          { signal },
        );
        all.push(...batch);
        if (batch.length < PAGE_SIZE) {
          break;
        }
      }
      return all;
    },
    enabled: Boolean(filters),
  });
}

/** Bulk upserts reward catalog entries and invalidates the entries cache. */
export function useUpsertRewardsEntries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkUpsertEntries,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rewardsQueryKeys.entries() });
    },
  });
}
